import { byId, cssEscape } from "./domUtils";
import type { PlanCode, Root } from "./prototypeTypes";
import { modalIds, viewAliases } from "./prototypeData";
import { clearBrowseReturnRestoreGuard, invalidateBrowseRequests, refreshBrowseGrid, suspendBrowsePaginationForReturn, syncInitialBrowseRouteIntent } from "./browseController";
import { DEFAULT_BROWSE_SORT } from './browseData';
import { mountedFor, stopVideoPlayerEnhancements, unmountMediaPlayer } from "./mediaPlayer";
import { isVideoPictureInPictureSessionActive } from './videoPictureInPicture';
import { backgroundDetachedVideoPresentationSurface, detachVideoPresentationSurface } from './videoPresentationSurface';
import { getRuntime } from "./runtimeAccessor";
import { showArticle } from "./articleController";
import { loadDynamicDataForView } from "./dynamicBindings";
import { showTopicDetail } from "./topicDetailView";
import { resetHydrateGuard } from "./hydrateGuard";
import { cancelThemeReveal, runThemeReveal } from "./themeReveal";
import { commitThemeMode, resolveThemeMode, syncBrowserChrome } from './themeBootstrapRuntime';
import { navigateWhenSsrViewMissing } from './ssrDirectNavigation';
import { activateModalAccessibility, deactivateModalAccessibility } from './modalAccessibility';
import { resolveInitialRoute } from './initialRoute';
import { feedbackTicketIdFromBrowserLocation } from './notificationActionRoute';
import { resumeActiveViewKeywordCloud, suspendInactiveViewKeywordCloud } from './viewKeywordCloudLifecycle';
import { disposeModalTurnstile, reconcileActiveModalTurnstile, reconcileModalTurnstile, reconcileVisibleWizardTurnstile } from './modalTurnstileLifecycle';
import { cancelTurnstilePrefetch } from './turnstileRuntime';
import { shouldUseIosDocumentThemeTransition } from './iosBrowserChromeThemePolicy';
import { runIosThemeTransition } from './iosThemeTransition';
import { resetSpaDocumentSeo } from './spaDocumentSeo';
import { consumeRequestedAiHistoryReturnState } from './aiCreateHistoryReturnState';
import { contextualBackNavigation } from './contextualBackNavigation';
import { getTopic, ensureTopic } from './topicStore';
import { activateCommunityTab, communityReturnStateFromHistory, resetCommunityEntryState, restoreCommunityReturnState } from './communityMountState';
import { ensureCredentialModalMounted, unmountCredentialModal } from './credentialModalLifecycle';
import { ensureBusinessOverlayMounted } from './businessOverlayLifecycle';
import { resetTransientSurfaceState } from './transientSurfaceState';
import { createBrowseHistoryEntryId, createBrowseHistoryEntryState, hasBrowseReturnSessionMarker, readBrowseHistorySnapshot, syncCurrentBrowseHistoryEntry } from './browsePaginationSessionStore';
import { deactivateHomeDiscovery, suspendHomePagedForReturn } from './homeDiscoveryHydrator';
import { homeReturnStateFromHistory } from './homeReturnState';
import { deactivateHomeViewHydration, prepareHomeReturnFrame } from './homeViewHydrator';
import { finishMobileBottomDockHistoryRestore, persistMobileBottomDockHistory, restoreMobileBottomDockHistory, restoreMobileBottomDockSnapshot } from './mobileBottomDockController';
import { resetAuthModalIntentNote } from './formProfileController';

// showArticle is re-exported for backward compatibility — it was originally
// imported via navigationController by several action route modules.
export { showArticle };

/**
 * Single-render: when true, showView() skips pushState so popstate-driven
 * view changes don't create duplicate history entries (infinite loop).
 */
let _suppressPushState = false;
const aiCreateViewGenerations = new WeakMap<Root, number>();

/** AI Create quota reads belong to the view entry that started them. */
export function currentAiCreateViewGeneration(root: Root): number {
	return aiCreateViewGenerations.get(root) ?? 0;
}

/**
 * When closeModal() calls history.back() to consume the modal's history
 * entry, the resulting popstate must NOT re-apply the view from the URL
 * (the view didn't actually change - only the modal closed). This flag
 * is set before history.back() and cleared in the popstate handler.
 */
let _popstateFromModalClose = false;
const CANONICAL_DESTINATION_DOCK = { dockState: 'expanded' as const, scrollY: 0 };

type MobileChapterDirectoryScrollLock = {
	scrollX: number;
	scrollY: number;
	bodyOverflow: string;
	bodyOverscrollBehavior: string;
	bodyPosition: string;
	bodyTop: string;
	bodyLeft: string;
	bodyWidth: string;
	htmlOverflow: string;
	htmlOverscrollBehavior: string;
};

// iOS Safari can keep the viewport's scroll chain on <html> even while
// body has overflow hidden. The chapter directory is the only mobile sheet
// that leaves a native scroll region exposed, so pin the document while it is
// open and restore the exact reading position on every close path.
let mobileChapterDirectoryScrollLock: MobileChapterDirectoryScrollLock | null = null;

function isMobileChapterDirectory(root: Root): boolean {
	return root.querySelector('#chapterDirectoryModal.active') !== null
		&& typeof window.matchMedia === 'function'
		&& window.matchMedia('(max-width: 640px)').matches;
}

function syncMobileChapterDirectoryScrollLock(root: Root): void {
	if (isMobileChapterDirectory(root)) {
		if (mobileChapterDirectoryScrollLock) return;
		const body = document.body;
		const html = document.documentElement;
		mobileChapterDirectoryScrollLock = {
			scrollX: window.scrollX,
			scrollY: window.scrollY,
			bodyOverflow: body.style.overflow,
			bodyOverscrollBehavior: body.style.overscrollBehavior,
			bodyPosition: body.style.position,
			bodyTop: body.style.top,
			bodyLeft: body.style.left,
			bodyWidth: body.style.width,
			htmlOverflow: html.style.overflow,
			htmlOverscrollBehavior: html.style.overscrollBehavior,
		};
		body.style.overflow = 'hidden';
		body.style.overscrollBehavior = 'none';
		body.style.position = 'fixed';
		body.style.top = `${-mobileChapterDirectoryScrollLock.scrollY}px`;
		body.style.left = `${-mobileChapterDirectoryScrollLock.scrollX}px`;
		body.style.width = '100%';
		html.style.overflow = 'hidden';
		html.style.overscrollBehavior = 'none';
		return;
	}

	const lock = mobileChapterDirectoryScrollLock;
	if (!lock) return;
	const body = document.body;
	const html = document.documentElement;
	body.style.overflow = lock.bodyOverflow;
	body.style.overscrollBehavior = lock.bodyOverscrollBehavior;
	body.style.position = lock.bodyPosition;
	body.style.top = lock.bodyTop;
	body.style.left = lock.bodyLeft;
	body.style.width = lock.bodyWidth;
	html.style.overflow = lock.htmlOverflow;
	html.style.overscrollBehavior = lock.htmlOverscrollBehavior;
	mobileChapterDirectoryScrollLock = null;
	window.scrollTo(lock.scrollX, lock.scrollY);
}

function reconcileModalBodyScrollLock(root: Root): void {
	// Release the directory's fixed-document lock before deriving the generic
	// modal lock. This preserves an underlying modal when the directory closes
	// first, while still restoring the pre-directory document styles when it was
	// the final surface.
	syncMobileChapterDirectoryScrollLock(root);
	document.body.style.overflow = root.querySelector('.modal-overlay.active') !== null ? 'hidden' : '';
}

export interface ViewHydrationOptions {
	hydrate?: boolean;
	socialSubTab?: 'community' | 'github';
	historyState?: Record<string, unknown>;
	historyMode?: 'push' | 'replace' | 'none';
}

function openVisitorLoginModal(root: Root): void {
	const authTransitionActive = root.querySelector('#registerModal.active, #forgotModal.active') !== null;
	if (!authTransitionActive) openModal(root, 'loginModal');
}

function rememberBrowseHistoryEntry(root: Root): void {
	const runtime = getRuntime(root);
	if (!runtime || typeof window === 'undefined' || !window.history) return;
	const state = root as unknown as { __browseFilter?: string; __browseSort?: 'default' | 'trending' | 'newest' };
	const filter = state.__browseFilter ?? 'all';
	const sort = state.__browseSort ?? DEFAULT_BROWSE_SORT;
	const search = (byId(root, 'browseSearchInput') as HTMLInputElement | null)?.value.trim() ?? '';
	ensureBrowseHistoryEntryId();
	// Generic Browse exits (tabs, Home, Profile…) only persist the current
	// scroll/filter snapshot. The only code allowed to mint a Topic-return
	// marker is learningActions' actual Browse-card → Topic transition.
	syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort, window.scrollY, { preserveReturnMarker: true });
}

function ensureBrowseHistoryEntryId(): void {
	if (typeof window === 'undefined' || !window.history) return;
	const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state as Record<string, unknown> : {};
	if (typeof currentState.browseHistoryEntryId === 'string' && currentState.browseHistoryEntryId.length > 0) return;
	window.history.replaceState({ ...currentState, browseHistoryEntryId: createBrowseHistoryEntryId() }, '', window.location.href);
}

/**
 * Map the current URL to an initial SPA view. Allows the backend to
 * serve `/topics/llms` or `/ai` and have the client mount the right
 * tab. Falls back to home if nothing matches.
 */
export function applyInitialViewFromUrl(
	root: Root,
	state?: { isLoggedIn?: boolean; currentPlan?: PlanCode },
	options: ViewHydrationOptions = {},
): boolean {
	if (typeof window === "undefined") return false;
	// A tab that stays open across a deploy can still hold an old /settings
	// history entry. Re-enter through the Worker so the document redirect is
	// the one compatibility authority; do not attempt to render the removed view.
	if (window.location.pathname.replace(/\/+$/u, '') === '/settings') {
		window.location.replace('/settings');
		return true;
	}
	const route = resolveInitialRoute(window.location.pathname);
	if (!route) {
		// Bare root path "/" -> Home. Without this, a popstate back to "/"
		// (e.g. iOS swipe-back from Topic) leaves the previous view active
		// because resolveInitialRoute returns null for the root path.
		// Skip the showView call if Home is already the active view (initial
		// load) to avoid a redundant pushState and to stay safe in test
		// environments where window.history may be undefined.
		const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
		if (!path) {
			// Skip the showView call if Home is already the active view (initial
			// load) to avoid a redundant pushState. Only call showView when there
			// is a real history API - test environments may stub window without
			// history, and showView would crash on pushState.
			const homeView = root.querySelector('#view-home');
			const homeActive = homeView?.classList.contains('active') === true;
			if (!homeActive && window.history) {
				showView('home', root);
			}
			setActiveTab('home', root);
			return true;
		}
		return false;
	}
	if (route.kind === "topic") {
		if (options.hydrate === false) {
			const rt = getRuntime(root);
			if (rt) rt.activeCourseId = route.slug;
			showView('topic', root, options);
			setActiveTab('home-browse', root);
			return true;
		}
		showTopicDetail(route.slug, root, state?.currentPlan ?? "free");
		setActiveTab("home-browse", root);
		return true;
	}
	if (route.kind === "chapter") {
		const rt = getRuntime(root);
		if (rt) {
			rt.activeCourseId = route.slug;
			rt.activeChapterIndex = route.chapter;
		}
		if (options.hydrate === false) {
			// Stamp __activeChapter so showPlayer() (triggered by the inline
			// "Play Audio" button) can populate the mini bar title/topic.
			// On this deep-link entry the article view is still a skeleton
			// ("Loading chapter…" / empty topic), so seed from the topic
			// store if cached and always refresh the stamp once the topic
			// index resolves - the index carries every chapter title and the
			// topic subtitle. Without this the mini bar falls back to the
			// literal "Chapter" + "DeepMox" placeholders.
			const seedTopic = getTopic(route.slug);
			const seedChapter = seedTopic
				? [...seedTopic.previewChapters, ...seedTopic.lockedChapters][route.chapter - 1]
				: undefined;
			const stamp = {
				index: route.chapter,
				title: seedChapter?.title ?? byId(root, 'article-title')?.textContent ?? 'Chapter',
				topicTitle: seedTopic?.title ?? byId(root, 'article-topic-context')?.textContent ?? 'DeepMox',
				topicSlug: route.slug,
				topicSubtitle: seedTopic?.subtitle ?? '',
			};
			(root as unknown as { __activeChapter?: typeof stamp }).__activeChapter = stamp;
			void ensureTopic(route.slug)
				.then(() => {
					const topic = getTopic(route.slug);
					if (!topic) return;
					const current = (root as unknown as { __activeChapter?: { topicSlug?: string } }).__activeChapter;
					if (current?.topicSlug !== route.slug) return;
					const chapter = [...topic.previewChapters, ...topic.lockedChapters][route.chapter - 1];
					(root as unknown as { __activeChapter?: typeof stamp }).__activeChapter = {
						index: route.chapter,
						title: chapter?.title ?? topic.title,
						topicTitle: topic.title,
						topicSlug: topic.slug,
						topicSubtitle: topic.subtitle ?? '',
					};
				})
				.catch(() => undefined);
			showView('article', root, options);
			setActiveTab('home-browse', root);
			return true;
		}
		showArticle(route.chapter, root, state?.currentPlan ?? "free");
		setActiveTab("home-browse", root);
		return true;
	}
	if (route.kind === "idea") {
		// Suppress showView's pushState: it would emit /idea-detail (the
		// generic fallback) because __activeIdeaSlug is intentionally not
		// set yet (see hydrator dual-paint guard below). We replaceState
		// the correct /community/ideas/<slug> URL ourselves so the address
		// bar stays refreshable. Mirrors how popstate suppresses pushState.
		const wasSuppressed = _suppressPushState;
		_suppressPushState = true;
		try {
			showView("idea-detail", root, options);
		} finally {
			_suppressPushState = wasSuppressed;
		}
		if (window.history) {
			window.history.replaceState({ view: "idea-detail" }, "", `/community/ideas/${encodeURIComponent(route.slug)}`);
		}
		setActiveTab("community", root);
		const rt = getRuntime(root);
		if (rt && options.hydrate !== false) {
			// Don't set __activeIdeaSlug before calling hydrateIdeaDetail -
			// the hydrator's dual-paint guard checks if the slug is already
			// set (meaning showIdeaDetail is fetching). On direct URL load
			// nothing else is fetching, so the slug must NOT be set yet or
			// the hydrator will skip its own fetch and leave the page on
			// "Loading idea…" forever. The hydrator sets the slug itself
			// via showIdeaDetail when it needs to.
			void import("./ideaDetailHydrator").then((mod) =>
				mod.hydrateIdeaDetail(root, rt.api, route.slug),
			);
		}
		return true;
	}
	// verify-email and reset-password are handled by PrototypeController
	// (they show full-screen overlays, not SPA views). Return false so
	// the controller knows to handle them itself.
	if (route.kind === "verify-email" || route.kind === "reset-password") {
		return false;
	}
	// Feedback is an account-only support surface.  Do this before showView()
	// so a direct URL (including a notification ticket URL) never briefly
	// renders its form or starts its view hydrator for a visitor.  Keep the
	// original URL intact: authenticated hydration re-applies the route once
	// the session is known, while a visitor remains on the login guidance.
	if (route.view === 'feedback' && state?.isLoggedIn !== true) {
		openVisitorLoginModal(root);
		return true;
	}
	// Profile and Membership & Billing are account-only surfaces. Visitors
	// must be stopped before their protected hydrators can create requests.
	if ((route.view === 'profile' || route.view === 'membership-billing') && state?.isLoggedIn !== true) {
		openVisitorLoginModal(root);
		return true;
	}
	if (route.socialSub) {
		showSocial(route.socialSub, root, options);
		return true;
	}
	// A valid Feedback notification deep link needs to survive the initial
	// paint. showView normally writes its canonical route and would otherwise
	// erase the ticket query before authenticated hydration can open it.
	const preserveFeedbackTicket = route.view === 'feedback' && feedbackTicketIdFromBrowserLocation(window.location) !== null;
	const checkoutSuccess = route.checkoutSuccess === true;
	// A direct redeem link carries the code in the fragment so it never reaches
	// the server or Referer header. showView() normally canonicalizes the URL
	// synchronously, while the redeem controller is loaded asynchronously; keep
	// the entry URL intact until that controller has persisted and cleaned it.
	const preserveRedeemEntry = route.view === 'redeem' && (window.location.hash !== '' || window.location.search !== '');
	// This path restores the view already represented by the address bar
	// (initial mount, session re-hydration, and popstate). It must never add
	// another identical entry: otherwise one browser Back appears to do nothing.
	const wasSuppressed = _suppressPushState;
	_suppressPushState = true;
	try {
		if (route.view === 'home-browse' && options.hydrate === false) syncInitialBrowseRouteIntent(root);
		showView(route.view, root, options);
	} finally {
		_suppressPushState = wasSuppressed;
	}
	if (checkoutSuccess) {
		window.history.replaceState({ view: 'profile' }, '', '/profile?checkout=success');
	}
	if (route.tab) setActiveTab(route.tab, root);
	return true;
}

export function initializePrototypeRuntimeState(root: Root) {
	// Player UI placeholders are driven by data-bind attributes in
	// playerMarkup; the actual chapter / topic title + thumb are
	// painted at runtime by playerUiController once a chapter is
	// selected. We must not hardcode any business copy (chapter
	// title, topic title, cover icon) here — that data lives in
	// D1 only and must come from /api/topics/:slug/chapters.
	//
	// The player starts hidden. showPlayer() (called when a chapter
	// is selected) flips both the .visible class on the bar AND
	// .has-player on the app layout (which adds the bottom padding
	// that keeps chapter lists from being covered). Showing the
	// bar on mount made it sit on top of the first chapter card on
	// the topic detail page when the page was shorter than the
	// viewport.
	byId(root, "resumeCard")?.classList.add("hidden");
}

export function showView(rawView: string, root: Root, options: ViewHydrationOptions = {}) {
	const view = viewAliases[rawView] ?? rawView;
	const initialPathname = window.location.pathname;
	const initialSearch = window.location.search;
	const initialHistoryState = window.history.state as { view?: unknown } | null;
	if (navigateWhenSsrViewMissing(root, view, routeForView(view, root), { preserveBrowseSnapshot: !_suppressPushState })) return;
	// Profile and Membership & Billing are account-only. Check before view
	// activation so a visitor cannot start either protected hydrator.
	if (view === "profile" || view === 'membership-billing') {
		const ps = readPrototypeState(root);
		if (ps?.isLoggedIn !== true) {
			openVisitorLoginModal(root);
			return;
		}
	}
	const previousView = root.querySelector<HTMLElement>('.view.active');
	const targetView = root.querySelector<HTMLElement>(`#view-${cssEscape(view)}`);
	if (previousView !== targetView && previousView?.querySelector('[data-community-mount]')) {
		// The Community vote warm-up is owned by its mounted page, not by the
		// process-wide runtime. Clear an unresolved challenge and its token cache
		// before this route can leave the Community surface behind.
		cancelTurnstilePrefetch(getRuntime(root), 'community-idea-vote');
	}
	const createsDestinationHistoryEntry = !_suppressPushState && options.historyMode !== 'none';
	// Persist the outgoing entry before any view/Dock geometry changes. A
	// history return restores this exact compact/expanded state, rather than
	// trying to infer it from the returned scroll offset.
	if (createsDestinationHistoryEntry) persistMobileBottomDockHistory(root);
	if (previousView?.id === 'view-home-browse' && view !== 'home-browse') {
		const preservesTopicReturn = view === 'topic' && hasBrowseReturnSessionMarker(window.history.state);
		if (preservesTopicReturn) suspendBrowsePaginationForReturn(root);
		// All Browse exits invalidate first-page ownership. A later Back may make
		// filter/scope/view match again, but it must never revive this request.
		invalidateBrowseRequests(root, { preserveReturnReserve: preservesTopicReturn });
		if (!_suppressPushState) rememberBrowseHistoryEntry(root);
		clearBrowseReturnRestoreGuard(root);
	}
	if (previousView?.id === 'view-home' && view !== 'home') {
		// A live page-two request owns skeletons that preserve the deep return
		// geometry. Freeze that pager first; only its converted hidden reserve is
		// allowed through the snapshot stability check below.
		const homeReturnSurface = homeReturnStateFromHistory(window.history.state)?.containerId;
		if (homeReturnSurface === 'recommended-list' || homeReturnSurface === 'mobileTrendingFeed') suspendHomePagedForReturn(root, homeReturnSurface);
		const completeHomeSnapshot = deactivateHomeViewHydration(root);
		const preservingHomeReturn = completeHomeSnapshot && (homeReturnSurface === 'recommended-list' || homeReturnSurface === 'mobileTrendingFeed');
		deactivateHomeDiscovery({ preserveRecommended: preservingHomeReturn });
	}
	if (view === 'home') {
		const state = readPrototypeState(root);
		if (state) prepareHomeReturnFrame(root, state);
	}
	const enteringView = targetView?.classList.contains('active') === false;
	if (targetView?.id === 'view-home-browse') targetView.classList.toggle('browse-return-preserved', hasBrowseReturnSessionMarker(window.history.state));
	if (previousView !== targetView) aiCreateViewGenerations.set(root, currentAiCreateViewGeneration(root) + 1);
	suspendInactiveViewKeywordCloud(previousView, view);
	// "community" and "github-trending" are real top-level views on desktop
	// (PC top-nav shows them as separate tabs above the fold). The mobile
	// bottom tab uses showSocial(...) directly to enter the aggregated
	// social view — it never calls showView('community'). Anything that
	// genuinely wanted the social aggregate (home rec deep links, idea
	// detail back button, etc.) should also call showSocial() explicitly.
	// Keeping this hook off preserves the PC ↔ mobile split: PC visitors
	// land on the dedicated community/github-trending page (and hit the
	// visitor gate as before); mobile visitors land on the social
	// aggregate via the bottom tab.

	root.querySelectorAll(".view").forEach((node) => node.classList.remove("active"));
	targetView?.classList.add("active");
	resumeActiveViewKeywordCloud(targetView);
	if (enteringView && targetView) resetTransientSurfaceState(targetView);
	// Single-render: use pushState for a real route transition so the back button works.
	// Hydrating a Topic/Chapter already represented by the address bar is not a
	// transition; replace its state in place or repeated hydration traps Back in
	// duplicate copies of the same Chapter URL.
	if (createsDestinationHistoryEntry) {
		const route = routeForView(view, root);
		const alreadyAtDetailRoute = (view === 'topic' || view === 'article')
			&& window.location.pathname.replace(/\/+$/u, '') === route.replace(/\/+$/u, '');
		if (alreadyAtDetailRoute || options.historyMode === 'replace') {
			const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
			window.history.replaceState({ ...currentState, ...options.historyState, view, mobileBottomDock: CANONICAL_DESTINATION_DOCK }, '', route);
		} else {
			const historyState = view === 'home-browse'
				? createBrowseHistoryEntryState(options.historyState)
				: { ...options.historyState, view };
			window.history.pushState({ ...historyState, mobileBottomDock: CANONICAL_DESTINATION_DOCK }, '', route);
		}
	}
	// Reset document head for SPA routes. Topic/Chapter/Idea-detail views
	// have their own dedicated SEO sync (syncTopicDocumentSeo /
	// syncChapterDocumentSeo / syncIdeaDocumentSeo) and must not be reset
	// here - that would wipe their structured data before the async fetch
	// completes.
	if (view !== "topic" && view !== "article" && view !== "idea-detail") {
		resetSpaDocumentSeo(window.location.pathname);
	}
	// Instant scroll on view switch: smooth scroll is unreliable when async
	// content (e.g. topic detail) loads after the call, leaving the page stuck
	// at the previous scroll position on mobile. Instant ensures every view
	// starts at the top.
	const preserveHomeReturnPosition = view === 'home' && homeReturnStateFromHistory(window.history.state) !== null;
	if (!(view === 'home-browse' && hasBrowseReturnSessionMarker(window.history.state)) && !preserveHomeReturnPosition) {
		window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
	}
	if (createsDestinationHistoryEntry) {
		// A new route begins at its own top/expanded baseline. The outgoing
		// entry was persisted above, so Forward must never inherit its compact
		// dock state merely because the previous view was scrolled.
		restoreMobileBottomDockSnapshot(root, CANONICAL_DESTINATION_DOCK);
		finishMobileBottomDockHistoryRestore(root);
	}
	// Audio is a global player: switching from article back to topic must not
	// reset playback. Video remains view-scoped because it is mounted inside the
	// full-screen surface.
	if (view !== "article") {
		const video = mountedFor(root, 'video');
		if (video?.element instanceof HTMLVideoElement && isVideoPictureInPictureSessionActive(video.element)) {
			// PiP owns presentation, not routing. Preserve this exact mounted
			// session so its route observer can restore only when it returns inline.
			backgroundDetachedVideoPresentationSurface(root);
		} else {
			stopVideoPlayerEnhancements(root);
			unmountMediaPlayer(root, "video");
		}
	}
	if (options.hydrate === false) {
		if (view === 'social' && options.socialSubTab) setSocialSubTab(options.socialSubTab, root, options);
		return;
	}
	if (view === 'home') {
		const state = readPrototypeState(root);
		if (state) void import('./homeViewHydrator').then((mod) => mod.hydrateActiveHome(root, state));
	}
	if (view === "home-browse") {
		ensureBrowseHistoryEntryId();
		// Session hydration can replay the current /browse route after a user
		// has typed a search. That replay refreshes access, not browse intent.
		const browseSearch = byId(root, "browseSearchInput") as HTMLInputElement | null;
		const browseState = root as unknown as { __browseFilter?: string; __browseSort?: 'default' | 'trending' | 'newest' };
		const browseSnapshot = readBrowseHistorySnapshot(window.history.state);
		if (browseSnapshot) {
			browseState.__browseFilter = browseSnapshot.filter;
			browseState.__browseSort = browseSnapshot.sort;
			if (browseSearch) browseSearch.value = browseSnapshot.search;
			const browseClear = byId(root, 'browseSearchClear') as HTMLElement | null;
			if (browseClear) browseClear.style.display = browseSnapshot.search ? '' : 'none';
		}
		const hasBrowseReturnSession = hasBrowseReturnSessionMarker(window.history.state);
		const isBareBrowseRouteReplay = enteringView
			&& initialHistoryState?.view === 'home-browse'
			&& initialPathname.replace(/\/+$/u, '') === '/browse'
			&& initialSearch === '';
		const preserveCurrentBrowseIntent = browseSnapshot !== null || hasBrowseReturnSession || (
			(!enteringView || isBareBrowseRouteReplay)
			&& window.location.pathname.replace(/\/+$/u, '') === '/browse'
			&& ((browseSearch?.value.trim() ?? '') !== '' || browseState.__browseFilter !== undefined)
		);
		if (!preserveCurrentBrowseIntent) {
		// Reset to initial state: default capsule "all", clear search.
		root.querySelectorAll<HTMLElement>("#browseFilters .category-pill").forEach((btn) =>
			btn.classList.toggle("active", (btn.dataset.protoClick ?? "").includes("'all'")),
		);
		if (browseSearch) browseSearch.value = "";
		const browseClear = byId(root, "browseSearchClear") as HTMLElement | null;
		if (browseClear) browseClear.style.display = "none";
		delete browseState.__browseFilter;
		}
		resetHydrateGuard("browse");
		const rt = getRuntime(root);
		if (rt) void refreshBrowseGrid(root, rt, { preserveActiveFilter: preserveCurrentBrowseIntent });
	}
	if (view === "idea-detail") {
		const rt = getRuntime(root);
		// No active idea id is on the view; the ideaDetailController
		// already shows the loading skeleton and fetches when
		// showIdeaDetail(slug) is called. This hook exists so a future
		// deeplink router can hand a slug into hydrateIdeaDetail.
		const slug = rt?.__activeIdeaSlug;
		if (rt && slug) {
			void import("./ideaDetailHydrator").then((mod) =>
				mod.hydrateIdeaDetail(root, rt.api, slug),
			);
		}
	}
	if (view === "ai-job-detail") {
		const rt = getRuntime(root);
		const jobId = rt?.__activeAiJobId;
		if (rt && jobId) {
			void import("./aiJobDetailController").then((mod) =>
				mod.hydrateAiJobDetail(root, rt.api, jobId),
			);
		}
	}
	if (view === "community") {
		// A trusted proposal return preserves its mount-local projection.
		// Direct Community entry and direct Idea Detail fallback still start clean.
		const returnState = communityReturnStateFromHistory(window.history.state);
		const targetFilter = returnState?.filter ?? 'completed';
		const commView = root.querySelector<HTMLElement>("#view-community.view");
		const commScope: ParentNode = (commView as ParentNode) ?? (root as ParentNode);
		if (commView) activateCommunityTab(commView, targetFilter);
		const communitySearch = commScope.querySelector<HTMLInputElement>('[data-community-role="search-input"]');
		if (communitySearch) communitySearch.value = returnState?.search ?? '';
		const communityClear = commScope.querySelector<HTMLElement>('[data-community-role="search-clear"]');
		if (communityClear) communityClear.style.display = returnState?.search ? '' : 'none';
		if (returnState) restoreCommunityReturnState(root, commView ?? undefined, returnState); else resetCommunityEntryState(root, commView ?? undefined);
		resetHydrateGuard("community:desktop");
		const rt = getRuntime(root);
		const ps = readPrototypeState(root);
		if (rt && ps) {
			void import("./viewHydrator").then((mod) => mod.hydrateCommunityView(root, rt, ps!, { scope: commView ?? undefined }));
		}
	}
	if (view === "github-trending") {
		// A contextual Topic Back lands on the original GitHub history entry.
		// Keep its capsule selection rather than overwriting it with the entry
		// default; direct navigation still starts from Completed.
		const historySegment = githubTrendingSegmentFromHistory(window.history.state);
		const activeSegment = historySegment ?? 'completed';
		const ghView = root.querySelector<HTMLElement>("#view-github-trending.view");
		const ghScope: ParentNode = (ghView as ParentNode) ?? (root as ParentNode);
		ghScope.querySelectorAll<HTMLElement>('[data-gh-role="filters"] .category-pill').forEach((btn) =>
			btn.classList.toggle("active", btn.dataset.ghFilter === activeSegment),
		);
		const ghSearch = ghScope.querySelector<HTMLInputElement>('[data-gh-role="search-input"]');
		if (ghSearch && !historySegment) ghSearch.value = "";
		const ghClear = ghScope.querySelector<HTMLElement>('[data-gh-role="search-clear"]');
		if (ghClear) ghClear.style.display = historySegment && ghSearch?.value.trim() ? '' : 'none';
		resetHydrateGuard("github-trending:desktop");
		const rt = getRuntime(root);
		if (rt) {
			// P4.2: isPaid 不再传递 - 所有身份都能看 Trending 列表
			void import("./githubTrendingHydrator").then((mod) =>
				mod.hydrateGithubTrending(root, rt.api, { scope: ghView ?? undefined }),
			);
		}
	}
	if (view === "profile") {
		const rt = getRuntime(root);
		const state = readPrototypeState(root);
		if (rt) void import("./profileHydrator").then((mod) => mod.hydrateProfile(root, rt.api, { isLoggedIn: state?.isLoggedIn === true }));
	}
	if (view === "history") {
		const rt = getRuntime(root);
		if (rt) void import("./historyViewHydrator").then((mod) => mod.hydrateHistoryView(root, rt.api));
	}
	if (view === 'membership-billing') {
		const rt = getRuntime(root);
		if (rt) void import('./membershipBillingHydrator').then((mod) => mod.hydrateMembershipBilling(root, rt.api));
	}
	if (view === 'redeem') {
		const state = readPrototypeState(root);
		void import('./promotionRedeemController').then(async (mod) => {
			mod.seedRedeemCodeFromFragment(root, state?.isLoggedIn ? state.runtime.userId : undefined);
			if (!state) return;
			const code = root.querySelector<HTMLInputElement>('#redeemCodeInput')?.value.trim().toUpperCase() ?? '';
			const preview = root.querySelector<HTMLElement>('#redeemPreview');
			const summary = root.querySelector<HTMLElement>('#redeemCodeSummary')?.textContent?.trim().toUpperCase() ?? '';
			if (code && (preview?.hidden !== false || summary !== code)) await mod.previewPromotionCode(root, state);
		});
	}
	if (view === 'referrals') {
		const rt = getRuntime(root);
		if (rt) void import('./referralCenterHydrator').then((mod) => mod.hydrateReferralCenter(root, rt.api));
	}
	if (view === 'messages') {
		const rt = getRuntime(root);
		const ps = readPrototypeState(root);
		if (rt) void import('./messagesPageHydrator').then((mod) => mod.hydrateMessagesPage(root, rt.api, {
			isLoggedIn: ps?.isLoggedIn === true,
			userId: ps?.isLoggedIn === true ? ps.runtime.userId || null : null,
		}));
	}
	if (view === "social") {
		// Default to community sub-tab when navigating to /social without a sub-tab
		const socialView = root.querySelector<HTMLElement>("#view-social");
		const activeSubTab = options.socialSubTab ?? socialView?.querySelector<HTMLElement>(".social-sub-tab.active")?.dataset.sub as "community" | "github" | undefined;
		if (activeSubTab) {
			setSocialSubTab(activeSubTab, root);
		} else {
			setSocialSubTab("community", root);
		}
	}
		if (view === "ai-create") {
			const returnState = consumeRequestedAiHistoryReturnState(root);
			const targetFilter = returnState?.filter ?? 'completed';
			// Ordinary navigation resets to Completed. Detail Back restores the
			// originating filter/search exactly once.
			root.querySelectorAll<HTMLElement>("#aiFilterTabs .category-pill").forEach((btn) =>
				btn.classList.toggle("active", (btn.dataset.protoClick ?? "").includes(`'${targetFilter}'`)),
			);
			const aiSearch = byId(root, "aiSearchInput") as HTMLInputElement | null;
			if (aiSearch) aiSearch.value = returnState?.search ?? '';
			const aiClear = byId(root, "aiSearchClear") as HTMLElement | null;
			if (aiClear) aiClear.style.display = returnState?.search ? '' : 'none';
			resetHydrateGuard("ai-create");
			const rt = getRuntime(root);
			const ps = readPrototypeState(root);
			if (rt && ps) {
				// All users (logged-in or not) see the AI Create page structure.
				// Generate button gating is handled at showAiCreateModal click.
				// Non-logged-in users see an empty course list with icon.
				const unlocked = true;
				toggleAiCreateGate(root, unlocked);
				if (ps.isLoggedIn) {
					void import("./aiCreateViewHydrator").then((mod) => mod.hydrateAiCreateView(rt, root, targetFilter));
				} else {
					// Show empty state for non-logged-in users
					const list = byId(root, "aiCreatedList");
					if (list) {
						list.innerHTML = `<div class="ai-created-empty"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin:0 auto 12px;display:block;opacity:0.6;"><path d="M12 2v4"></path><path d="M12 18v4"></path><path d="m4.93 4.93 2.83 2.83"></path><path d="m16.24 16.24 2.83 2.83"></path><path d="M2 12h4"></path><path d="M18 12h4"></path><path d="m4.93 19.07 2.83-2.83"></path><path d="m16.24 7.76 2.83-2.83"></path></svg><div class="ai-created-empty-title">No private topics yet</div><div class="ai-created-empty-body">Sign in to create and track private topics.</div></div>`;
					}
					const usedEl = byId(root, "aiQuotaUsed");
					const totalEl = byId(root, "aiQuotaTotal");
					if (usedEl) usedEl.textContent = "0";
					if (totalEl) totalEl.textContent = "0";
				}
				// Load dynamic bindings (siteSettings for paywall metrics)
				void loadDynamicDataForView(root, { view: "ai-create", plan: ps.currentPlan ?? "visitor" }, rt.api);
			}
		}
		if (view === "feedback") {
		const rt = getRuntime(root);
		const ps = readPrototypeState(root);
		void loadDynamicDataForView(root, { view: "feedback", plan: ps?.currentPlan ?? "visitor" }, rt?.api);
	}
}

function githubTrendingSegmentFromHistory(state: unknown): 'daily' | 'weekly' | 'monthly' | 'completed' | null {
	if (!state || typeof state !== 'object') return null;
	const segment = (state as Record<string, unknown>).githubTrendingSegment;
	return segment === 'daily' || segment === 'weekly' || segment === 'monthly' || segment === 'completed' ? segment : null;
}

/** Enters a child page with a runtime-scoped marker that authorizes history.back(). */
export function showContextualView(rawView: string, root: Root, options: ViewHydrationOptions = {}): void {
	const view = viewAliases[rawView] ?? rawView;
	const route = routeForView(view, root).replace(/\/+$/u, '');
	const alreadyAtRoute = window.location.pathname.replace(/\/+$/u, '') === route;
	// Only carry explicit, route-owned return state into a contextual child.
	// Copying the whole current state can leak a modalOpen marker into the
	// child, which makes Browser Back close an already-gone dialog.
	const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
	const communityReturnState = communityReturnStateFromHistory(currentState);
	const historyState = { ...(communityReturnState ? { communityReturnState } : {}), ...options.historyState };
	showView(rawView, root, {
		...options,
		// A direct route (or a repaired Article route) is already represented by
		// this entry. Do not retrofit it with a marker and authorize an unsafe Back.
		historyState: alreadyAtRoute ? historyState : contextualBackNavigation.markChildEntry(historyState),
	});
}

/**
 * Restores a trusted in-app parent without manufacturing a new history entry.
 * Direct links, refreshed tabs, and stale state fall back in-place.
 */
export function backFromContextualView(
	root: Root,
	fallback: { view: string; tab: string },
	onFallback?: () => void,
): boolean {
	if (contextualBackNavigation.backDecision(window.history.state).kind === 'history-back') {
		window.history.back();
		return true;
	}
	if (onFallback) onFallback();
	else {
		showView(fallback.view, root, { historyMode: 'replace' });
		setActiveTab(fallback.tab, root);
	}
	return false;
}

/**
 * Toggle the AI Create gate vs the unlocked body, and clear the
 * always-present skeleton list for gated users so the shimmering
 * placeholders don't animate forever on a Free/Lite/visitor view
 * (the list lives outside both containers in the markup by design,
 * so the gate toggle must explicitly clear it).
 *
 */
function toggleAiCreateGate(root: Root, unlocked: boolean): void {
	const gate = root.querySelector<HTMLElement>("#aiCreateGate");
	const body = root.querySelector<HTMLElement>("#aiCreateUnlocked");
	const list = byId(root, "aiCreatedList");
	if (gate) gate.style.display = unlocked ? "none" : "";
	if (body) body.style.display = unlocked ? "" : "none";
	if (!unlocked && list) list.innerHTML = "";
}

export function setActiveTab(rawView: string, root: Root) {
	const view = viewAliases[rawView] ?? rawView;
	root.querySelectorAll(".nav-tab").forEach((node) => node.classList.remove("active"));
	root.querySelector(`.nav-tab[data-view="${cssEscape(view)}"]`)?.classList.add("active");
	// Sync bottom tab nav (mobile) — uses data-tab attribute on .tab-item
	root.querySelectorAll(".tab-item").forEach((node) => node.classList.remove("active"));
	root.querySelector(`.tab-item[data-tab="${cssEscape(view)}"]`)?.classList.add("active");
}

/**
 * Social view entry point - shows the social view and sets the active sub-tab.
 * Called from bottom tab "Social" button, desktop "Community"/"GitHub Trending" nav tabs,
 * and deep links (/social, /social/community, /social/github).
 */
export function showSocial(subTab: "community" | "github", root: Root, options: ViewHydrationOptions = {}) {
	showView("social", root, { ...options, socialSubTab: subTab });
	// Activate desktop nav tab for the sub-tab
	root.querySelectorAll(".nav-tab").forEach((node) => node.classList.remove("active"));
	root.querySelector(`.nav-tab[data-view="${cssEscape(subTab === "community" ? "community" : "github-trending")}"]`)?.classList.add("active");
	// Activate bottom "social" tab
	root.querySelectorAll(".tab-item").forEach((node) => node.classList.remove("active"));
	root.querySelector(`.tab-item[data-tab="social"]`)?.classList.add("active");
}

/**
 * Smart back-navigation from idea-detail to the community view.
 *
 * PC (width > 1024px) uses dedicated top-level views (#view-community /
 * #view-github-trending); mobile uses the aggregated #view-social with
 * sub-tabs. The idea-detail back button was hardcoded to showSocial(),
 * which on PC wrongly activated #view-social instead of #view-community.
 * This picks the correct destination based on the viewport.
 */
export function backToCommunity(root: Root): void {
	const isMobile = window.matchMedia?.("(max-width: 1024px)").matches ?? window.innerWidth <= 1024;
	backFromContextualView(root, { view: 'community', tab: 'community' }, () => {
		if (isMobile) showSocial("community", root, { historyMode: 'replace' });
		else {
			showView("community", root, { historyMode: 'replace' });
			setActiveTab("community", root);
		}
	});
}

/**
 * Toggle the active social sub-tab (community or github).
 * Updates the URL to /social/community or /social/github.
 */
export function setSocialSubTab(subTab: "community" | "github", root: Root, options: ViewHydrationOptions = {}) {
	const socialView = root.querySelector<HTMLElement>("#view-social");
	if (!socialView) return;
	if (subTab !== 'community') cancelTurnstilePrefetch(getRuntime(root), 'community-idea-vote');

	// Toggle active class on sub-tab buttons
	socialView.querySelectorAll(".social-sub-tab").forEach((btn) => {
		const htmlBtn = btn as HTMLElement;
		htmlBtn.classList.toggle("active", htmlBtn.dataset.sub === subTab);
	});

	// Toggle active class on sub-panels
	socialView.querySelectorAll(".social-panel").forEach((panel) => {
		const htmlPanel = panel as HTMLElement;
		htmlPanel.classList.toggle("active", htmlPanel.dataset.subPanel === subTab);
	});

	// Update URL
	const url = subTab === "community" ? "/social/community" : "/social/github";
	// Keep a contextual Topic return's GitHub segment. Replacing the state
	// with null here made mobile lose the Completed marker that desktop uses
	// when Back restores /social/github.
	const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
	window.history.replaceState({ ...currentState, view: 'social' }, "", url);
	if (options.hydrate === false) return;

	// Trigger hydrator for the active sub-tab
	if (subTab === "community") {
		// The Social mount owns independent filters, search and render state.
		// A proposal return is the sole contextual exception.
		const returnState = communityReturnStateFromHistory(window.history.state);
		const targetFilter = returnState?.filter ?? 'completed';
		const commPanel = socialView?.querySelector<HTMLElement>('.social-panel[data-sub-panel="community"]');
		const commScope: ParentNode = (commPanel as ParentNode) ?? (root as ParentNode);
		if (commPanel) activateCommunityTab(commPanel, targetFilter);
		const communitySearch = commScope.querySelector<HTMLInputElement>('[data-community-role="search-input"]');
		if (communitySearch) communitySearch.value = returnState?.search ?? '';
		const communityClear = commScope.querySelector<HTMLElement>('[data-community-role="search-clear"]');
		if (communityClear) communityClear.style.display = returnState?.search ? '' : 'none';
		if (returnState) restoreCommunityReturnState(root, commPanel ?? undefined, returnState); else resetCommunityEntryState(root, commPanel ?? undefined);
		resetHydrateGuard("community:social");
		const rt = getRuntime(root);
		const ps = readPrototypeState(root);
		if (rt && ps) {
			void import("./viewHydrator").then((mod) => mod.hydrateCommunityView(root, rt, ps!, { scope: commPanel ?? undefined }));
		}
	} else {
		// A normal Social entry starts at Completed. A contextual Topic Back carries
		// the selected segment in its history entry and must restore it instead.
		const ghPanel = socialView?.querySelector<HTMLElement>('.social-panel[data-sub-panel="github"]');
		const ghScope: ParentNode = (ghPanel as ParentNode) ?? (root as ParentNode);
		const githubSegment = githubTrendingSegmentFromHistory(window.history.state);
		const activeGithubSegment = githubSegment ?? 'completed';
		ghScope.querySelectorAll<HTMLElement>('[data-gh-role="filters"] .category-pill').forEach((btn) =>
			btn.classList.toggle("active", btn.dataset.ghFilter === activeGithubSegment),
		);
		const ghSearch = ghScope.querySelector<HTMLInputElement>('[data-gh-role="search-input"]');
		if (ghSearch && !githubSegment) ghSearch.value = "";
		const ghClear = ghScope.querySelector<HTMLElement>('[data-gh-role="search-clear"]');
		if (ghClear && !githubSegment) ghClear.style.display = "none";
		resetHydrateGuard("github-trending:social");
		const rt = getRuntime(root);
		const ps = readPrototypeState(root);
		if (rt) {
			const isPaid = ps ? (ps.currentPlan === "pro" || ps.currentPlan === "max") : false;
			void import("./githubTrendingHydrator").then((mod) =>
				mod.hydrateGithubTrending(root, rt.api, { isPaid, scope: ghPanel ?? undefined }),
			);
		}
	}
}

export function setThemeMode(mode: string, root: Root, animate = true) {
	const resolution = resolveThemeMode(mode);
	const resolved = mode === "auto" ? (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : mode;
	const currentTheme = document.documentElement.dataset.theme;
	const syncThemeControls = () => {
		root.querySelectorAll("[data-theme-mode]").forEach((node) => node.classList.toggle("active", (node as HTMLElement).dataset.themeMode === mode));
	};
	const applyTheme = () => {
		commitThemeMode(mode);
		syncThemeControls();
	};
	const runInPageThemeChange = () => {
		// Non-iOS behavior intentionally retains the established ordering and
		// View Transition implementation.
		syncBrowserChrome(mode);
		cancelThemeReveal();
		if (!animate && currentTheme === resolved) {
			syncThemeControls();
			return;
		}
		if (!animate || !currentTheme || currentTheme === resolved || matchMedia("(prefers-reduced-motion: reduce)").matches) {
			applyTheme();
			return;
		}
		runThemeReveal({ ballColor: resolution.background, applyTheme });
	};

	// On iOS/iPadOS WebKit, browser chrome only resamples reliably across a
	// document navigation. Keep the old document visually untouched while the
	// covered navigation transaction persists and prepaints the target theme.
	if (shouldUseIosDocumentThemeTransition({ animate, currentTheme, resolvedTheme: resolution.theme })) {
		cancelThemeReveal();
		if (runIosThemeTransition({ mode, targetBackground: resolution.background, root, onFallback: runInPageThemeChange })) return;
	}

	runInPageThemeChange();
}

export function openModal(root: Root, id: string, returnFocus?: HTMLElement | null, options?: { pushHistory?: boolean }) {
	const modal = byId(root, id) ?? ensureBusinessOverlayMounted(root, id) ?? ensureCredentialModalMounted(root, id);
	if (!modal) return;
	const enteringModal = !modal.classList.contains('active');
	if (enteringModal && id === 'loginModal') resetAuthModalIntentNote(root, 'login');
	if (enteringModal && id === 'registerModal') resetAuthModalIntentNote(root, 'register');
	modal.hidden = false;
	modal.classList.add("active");
	// Lock before modal focus management. On mobile, focusing the dialog can
	// otherwise nudge the reading viewport before the directory captures it.
	// The directory lock snapshots inline document styles so it can restore the
	// exact reading position. It must run before the generic modal lock writes
	// overflow:hidden, otherwise closing the sheet faithfully restores that
	// transient value and leaves the whole page frozen.
	syncMobileChapterDirectoryScrollLock(root);
	document.body.style.overflow = "hidden";
	if (enteringModal) resetTransientSurfaceState(modal);
	activateModalAccessibility(root, modal, () => requestModalClose(root, modal, id), returnFocus);
	void reconcileModalTurnstile(root, id);
	// Push a history entry so iOS Safari edge-swipe-back closes the modal
	// instead of navigating away from the page. The popstate handler
	// (wirePopStateNavigation) detects the modal state and closes it.
	// pushHistory:false folds the modal into the entry of the modal below it
	// (used by the resume prompt opened on top of the upgrade confirm), so the
	// lower modal's own close pops a single clean entry with no orphan.
	if (window.history && !modal.dataset.historyPushed && options?.pushHistory !== false) {
		modal.dataset.historyPushed = "1";
		// A modal owns only its own marker. Preserve the narrowly-scoped
		// Community return projection so a dialog can hand off to Topic and
		// Browser Back still rebuilds the originating tab/search/scroll.
		const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
		const communityReturnState = communityReturnStateFromHistory(currentState);
		window.history.pushState({ ...(communityReturnState ? { communityReturnState } : {}), modalOpen: id }, "");
	}
}

function requestModalClose(root: Root, modal: HTMLElement, id: string): void {
	const closeControl = Array.from(modal.querySelectorAll<HTMLElement>('[data-proto-click]')).find((element) =>
		/\bclose[A-Z]|\bcloseModal\b/.test(element.dataset.protoClick ?? ''));
	if (!closeControl) {
		closeModal(root, id);
		return;
	}
	closeControl.click();
	// The application router handles product-specific close controls
	// synchronously. Keep the native close fallback for isolated surfaces
	// (and tests) where no delegated action handler is mounted.
	if (modal.classList.contains('active')) closeModal(root, id);
}

/**
 * Hands an open reader modal's navigation action (e.g. View Topic) to the app
 * router only after the modal's own history entry has been consumed. This
 * prevents a child entry from inheriting `modalOpen`, and leaves Browser Back
 * on the modal's original return entry. `close` must run the modal's full
 * product-specific teardown (abort in-flight reads, restore scoped history
 * projections) — it runs before `navigate` in every path.
 */
export function handoffModalForNavigation(root: Root, modalId: string, close: () => void, navigate: () => void): void {
	const modal = byId(root, modalId);
	if (!modal?.classList.contains('active')) {
		navigate();
		return;
	}
	const waitsForHistoryExit = modal.dataset.historyPushed === '1' && window.history.state?.modalOpen === modalId;
	const finish = () => {
		modal.hidden = true;
		navigate();
	};
	if (!waitsForHistoryExit) {
		close();
		finish();
		return;
	}
	const onPopState = () => {
		if (modal.classList.contains('active') || window.history.state?.modalOpen === modalId) return;
		window.removeEventListener('popstate', onPopState);
		finish();
	};
	window.addEventListener('popstate', onPopState);
	close();
}

export function closeModal(root: Root, id: string) {
	const modal = byId(root, id);
	if (modal) {
		disposeModalTurnstile(root, id);
		modal.classList.remove("active");
		deactivateModalAccessibility(root, modal);
		// If this modal pushed a history entry, pop it so the address bar
		// stays in sync. Only do this when the history state still points
		// to this modal (i.e. we're closing via button, not via popstate
		// which already consumed the entry).
		if (modal.dataset.historyPushed) {
			delete modal.dataset.historyPushed;
			if (window.history.state?.modalOpen === id) {
				_popstateFromModalClose = true;
				window.history.back();
			}
		}
		unmountCredentialModal(root, id);
		// After closing a protected modal (e.g. Forgot Password opened on
		// top of Login), restore the Turnstile challenge on the remaining
		// topmost active protected modal. If no protected modal remains,
		// fall through to wizard restoration.
		void reconcileActiveModalTurnstile(root);
		void reconcileVisibleWizardTurnstile(root);
	}
	reconcileModalBodyScrollLock(root);
}

export function closeAllModals(root: Root) {
	const ids = new Set(modalIds);
	root.querySelectorAll<HTMLElement>('.modal-overlay[id]').forEach((modal) => ids.add(modal.id));
	// Count how many modals have a history entry so we can pop them all
	let historyPopsNeeded = 0;
	for (const id of ids) {
		const modal = byId(root, id);
		if (modal?.dataset.historyPushed) {
			delete modal.dataset.historyPushed;
			historyPopsNeeded++;
		}
		closeModal(root, id);
	}
	reconcileModalBodyScrollLock(root);
	// Pop the history entries for all modals that pushed one
	if (historyPopsNeeded > 0 && window.history.state?.modalOpen) {
		_popstateFromModalClose = true;
		window.history.go(-historyPopsNeeded);
	}
}

/**
 * Remove a bounded modal stack before navigating to a new SPA route. Unlike
 * closeModal(), this consumes every associated history entry in one operation
 * and waits for that popstate before the caller writes its new route.
 */
export function closeModalStackForRoute(root: Root, ids: readonly string[]): Promise<void> {
	let historyPopsNeeded = 0;
	for (const id of ids) {
		const modal = byId(root, id);
		if (!modal) continue;
		disposeModalTurnstile(root, id);
		modal.classList.remove('active');
		deactivateModalAccessibility(root, modal);
		if (modal.dataset.historyPushed) {
			delete modal.dataset.historyPushed;
			historyPopsNeeded++;
		}
		unmountCredentialModal(root, id);
	}
	reconcileModalBodyScrollLock(root);
	void reconcileActiveModalTurnstile(root);
	void reconcileVisibleWizardTurnstile(root);
	if (historyPopsNeeded === 0 || !window.history?.state?.modalOpen) return Promise.resolve();
	return new Promise((resolve) => {
		const settle = () => {
			window.removeEventListener('popstate', settle);
			resolve();
		};
		window.addEventListener('popstate', settle, { once: true });
		window.history.go(-historyPopsNeeded);
	});
}

/** Close stale sign-in transition overlays without dismissing a user-opened product modal. */
export function closeAuthenticationModals(root: Root): void {
	for (const id of ['loginModal', 'registerModal', 'forgotModal', 'oauthLinkModal']) closeModal(root, id);
}

export function routeForView(view: string, root: Root) {
	if (view === "home") return "/";
	if (view === "home-browse") return "/browse";
	if (view === "ai-create") return "/ai";
	if (view === 'referrals') return '/referrals';
	if (view === 'messages') return '/messages';
	if (view === "social") {
		// Determine the currently active social sub-tab to build the URL
		const socialView = root.querySelector<HTMLElement>("#view-social");
		const activeSubTab = socialView?.querySelector<HTMLElement>(".social-sub-tab.active")?.dataset.sub;
		return activeSubTab === "github" ? "/social/github" : "/social/community";
	}
	if (view === "idea-detail") {
		// Use the real, server-recognized URL so a hard refresh serves the
		// SPA shell via ideaSeoRoute (browser) and re-hydrates the post.
		// Mirrors the topic/article pattern; __activeIdeaSlug is stamped by
		// showIdeaDetail before showView is called.
		const rt = getRuntime(root);
		if (rt?.__activeIdeaSlug) return `/community/ideas/${encodeURIComponent(rt.__activeIdeaSlug)}`;
	}
	if (view === "topic") {
		const rt = getRuntime(root);
		if (rt?.activeCourseId) return `/topics/${encodeURIComponent(rt.activeCourseId)}`;
	}
	if (view === "article") {
		const rt = getRuntime(root);
		if (rt?.activeCourseId && Number.isInteger(rt.activeChapterIndex) && rt.activeChapterIndex > 0) {
			return `/topics/${encodeURIComponent(rt.activeCourseId)}/chapters/${rt.activeChapterIndex}`;
		}
	}
	return `/${view}`;
}

/**
 * Re-hydrate the currently active view after an auth state change
 * (login / register). Unlike `showView`, this does NOT toggle `.active`
 * classes, replaceState the URL, scrollTo, or unmount the media player -
 * it only re-runs the data hydrator for the current view so the DOM
 * reflects the new logged-in / plan state.
 *
 * Views not listed here (topic / article / social / idea-detail /
 * ai-job-detail) are intentionally skipped: topic/article have a live
 * player that re-hydration would disrupt, social is a mobile aggregate
 * whose sub-tabs manage their own state, and detail views require a
 * slug/jobId that isn't available from the DOM alone.
 */
export function rehydrateCurrentView(root: Root): void {
	const activeView = root.querySelector<HTMLElement>(".view.active");
	if (!activeView) return;
	const view = activeView.id?.replace(/^view-/, "");
	if (!view) return;

	const rt = getRuntime(root);
	const ps = readPrototypeState(root);
	if (!rt) return;

	switch (view) {
		case "home":
			// Home is already handled by hydrateActiveHome in routeAuthAction.
			break;
		case "home-browse":
			resetHydrateGuard("browse");
			void refreshBrowseGrid(root, rt);
			break;
		case "community": {
			resetHydrateGuard("community:desktop");
			if (ps) {
				void import("./viewHydrator").then((mod) =>
					mod.hydrateCommunityView(root, rt, ps, { scope: activeView ?? undefined }),
				);
			}
			break;
		}
		case "github-trending": {
			resetHydrateGuard("github-trending:desktop");
			const isPaid = ps ? (ps.currentPlan === "pro" || ps.currentPlan === "max") : false;
			void import("./githubTrendingHydrator").then((mod) =>
				mod.hydrateGithubTrending(root, rt.api, { isPaid, scope: activeView ?? undefined }),
			);
			break;
		}
		case "profile":
			if (ps?.isLoggedIn === true) {
				void import("./profileHydrator").then((mod) => mod.hydrateProfile(root, rt.api, { isLoggedIn: true }));
			}
			break;
		case "history":
			void import("./historyViewHydrator").then((mod) => mod.hydrateHistoryView(root, rt.api));
			break;
		case 'membership-billing':
			void import('./membershipBillingHydrator').then((mod) => mod.hydrateMembershipBilling(root, rt.api));
			break;
		case 'redeem':
			if (ps?.isLoggedIn === true) {
				void import('./promotionRedeemController').then(async (mod) => {
					mod.seedRedeemCodeFromFragment(root, ps.runtime.userId || undefined);
					await mod.previewPromotionCode(root, ps);
				});
			}
			break;
		case 'referrals':
			void import('./referralCenterHydrator').then((mod) => mod.hydrateReferralCenter(root, rt.api));
			break;
		case 'messages':
			void import('./messagesPageHydrator').then((mod) => mod.hydrateMessagesPage(root, rt.api, {
				isLoggedIn: ps?.isLoggedIn === true,
				userId: ps?.isLoggedIn === true ? ps.runtime.userId || null : null,
			}));
			break;
		case "ai-create": {
			resetHydrateGuard("ai-create");
			if (ps) {
				// All users see the AI Create page structure.
				const unlocked = true;
				toggleAiCreateGate(root, unlocked);
				if (ps.isLoggedIn) {
					void import("./aiCreateViewHydrator").then((mod) =>
						mod.hydrateAiCreateView(rt, root, mod.currentAiFilter(root)),
					);
				} else {
					const list = byId(root, "aiCreatedList");
					if (list) {
						list.innerHTML = `<div class="ai-created-empty"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin:0 auto 12px;display:block;opacity:0.6;"><path d="M12 2v4"></path><path d="M12 18v4"></path><path d="m4.93 4.93 2.83 2.83"></path><path d="m16.24 16.24 2.83 2.83"></path><path d="M2 12h4"></path><path d="M18 12h4"></path><path d="m4.93 19.07 2.83-2.83"></path><path d="m16.24 7.76 2.83-2.83"></path></svg><div class="ai-created-empty-title">No private topics yet</div><div class="ai-created-empty-body">Sign in to create and track private topics.</div></div>`;
					}
					const usedEl = byId(root, "aiQuotaUsed");
					const totalEl = byId(root, "aiQuotaTotal");
					if (usedEl) usedEl.textContent = "0";
					if (totalEl) totalEl.textContent = "0";
				}
				// Load dynamic bindings (siteSettings for paywall metrics)
				void loadDynamicDataForView(root, { view: "ai-create", plan: ps.currentPlan ?? "visitor" }, rt.api);
			}
			break;
		}
		case "feedback":
			if (ps) {
				void loadDynamicDataForView(root, { view: "feedback", plan: ps.currentPlan ?? "visitor" }, rt.api);
			}
			break;
		case "article":
		case "topic":
			// SSR-rendered Topic/Chapter documents bake the viewer's entitlements
			// into the paywall DOM and hand out session-bound playback URLs. A
			// login (or OAuth link) that succeeded on such a page is only
			// reflected by a fresh document render, so reload instead of
			// patching the locked state in place.
			window.location.reload();
			break;
		default:
			break;
	}
}

function readPrototypeState(root: Root): import("./controllerState").PrototypeState | undefined {
	return (root as unknown as { __prototypeState?: import("./controllerState").PrototypeState }).__prototypeState;
}

/**
 * Single-render: wire popstate listener so the browser back/forward buttons
 * restore the correct view. The old code used replaceState (no history
 * entries pushed), so back always left the site. Now that showView uses
 * pushState, popstate fires on back/forward and we re-apply the view
 * from the URL without pushing a new history entry.
 */
export function wirePopStateNavigation(root: Root): () => void {
	// Browsers restore a history entry's scroll after dispatching popstate. That
	// late native commit used to arrive after the dock guard had been released,
	// which made an expanded dock at a deep saved position read 0 → deep as a
	// fresh down-scroll. Take ownership for the SPA lifetime instead: every
	// entry that carries a dock snapshot is committed explicitly below, before
	// the guard is released.
	const priorScrollRestoration = window.history.scrollRestoration;
	window.history.scrollRestoration = 'manual';
	const onPopState = () => {
		// If this popstate was triggered by closeModal()'s history.back(),
		// the view underneath didn't change - just consume the event.
		if (_popstateFromModalClose) {
			_popstateFromModalClose = false;
			// A modal closed while a higher modal was on top orphans its
			// history entry (state still says modalOpen but the modal is no
			// longer active). Walk past any such liar entries so the stack
			// lands on the real page view — otherwise the next unconsumed
			// popstate re-applies a stale URL (the recurring jump to
			// /membership-billing after a paid upgrade).
			const state = window.history.state;
			if (state?.modalOpen) {
				const modal = root.querySelector<HTMLElement>(`#${state.modalOpen}`);
				if (!modal || !modal.classList.contains('active')) {
					_popstateFromModalClose = true;
					window.history.back();
				}
			}
			return;
		}
		// If a modal is open and the user hit back (e.g. iOS edge swipe),
		// close all modals instead of navigating away from the page.
		const activeModal = root.querySelector<HTMLElement>('.modal-overlay.active');
		if (activeModal?.id) {
			requestModalClose(root, activeModal, activeModal.id);
			return;
		}
		// Browser Back while the video fullscreen is open must close it
		// exactly like the player's Back button (transfer to audio +
		// return to the article), instead of leaving a stale black overlay
		// while the URL re-applies a chapter route underneath it.
		//
		// Previously this dispatched `#vfBackBtn.click()`. That worked when
		// the click reached the button, but on a fast Back press the click
		// sometimes fired before the gesture controller's `hide()` had
		// settled, leaving the video mounted with no listener — a stale
		// black surface that ignored further taps. Calling the transfer
		// helper directly is idempotent (it always tears down the video
		// and re-mounts the audio player) and skips the click round-trip.
		const videoFullscreen = root.querySelector<HTMLElement>('#videoFullscreen');
		if (videoFullscreen?.classList.contains('active')) {
			const mountedVideo = mountedFor(root, 'video');
			if (mountedVideo?.element instanceof HTMLVideoElement && isVideoPictureInPictureSessionActive(mountedVideo.element)) {
				// A live system PiP window dies with its element. Back during PiP
				// is a plain navigation, exactly like on desktop where the already
				// detached surface misses this active check: detach the leftover
				// active surface (the enter probe can race the mode flip) and let
				// the route apply underneath the floating window.
				detachVideoPresentationSurface(root);
				backgroundDetachedVideoPresentationSurface(root);
			} else {
				const state = readPrototypeState(root);
				if (state) {
					// Mirror the video's actual play state onto the audio player,
					// exactly like the player's Back button (closeVideoFullscreen):
					// a video that was playing hands off to playing audio, not a
					// paused player that surprises the user after a browser Back.
					const mounted = mountedFor(root, 'video');
					const wasPlaying = mounted ? !mounted.element.paused : false;
					void import('./actionRoutes/mediaRouteHelpers').then(({ transferVideoProgressToAudio }) =>
						transferVideoProgressToAudio(root, state, { play: wasPlaying }),
					);
				}
				return;
			}
		}
		_suppressPushState = true;
		try {
			// Restore the history dock before activating the target view. The
			// controller keeps one no-transition frame while native scroll
			// restoration settles, then rebases its directional accumulator.
			const pendingBrowseReturn = hasBrowseReturnSessionMarker(window.history.state);
			const dockSnapshot = restoreMobileBottomDockHistory(root, window.history.state);
			applyInitialViewFromUrl(root, readPrototypeState(root));
			// A trusted Browse return owns a preserved grid and commits its target
			// scroll after its synchronous double-frame layout reconciliation. Every
			// other view commits the entry snapshot now; with native restoration
			// disabled this is the only scroll write and therefore has a defined end.
			if (pendingBrowseReturn && getRuntime(root)) return;
			if (dockSnapshot) window.scrollTo({ top: dockSnapshot.scrollY, behavior: 'instant' as ScrollBehavior });
			finishMobileBottomDockHistoryRestore(root);
		} finally {
			_suppressPushState = false;
		}
	};
	window.addEventListener("popstate", onPopState);
	return () => {
		window.removeEventListener("popstate", onPopState);
		// Do not leave a detached test/controller owning the browser's scroll
		// policy. A concurrently installed owner may already have changed it.
		if (window.history.scrollRestoration === 'manual') window.history.scrollRestoration = priorScrollRestoration;
	};
}
